import React, { useContext, useReducer } from 'react'
import { AudioEngineLocalStreamStats } from './core/AudioEngine'
import {
    AudioInputError,
    AudioInputSelection,
    AudioInputStreamInfo,
    AudioSourceInfo,
    DesktopCapturerSourceExt,
    ExtraVideoOptions,
} from './UserContext'
import { Features } from './services/CanaryFeatures'
import { RepeatRule } from './pages/ScheduleSessionPage/ScheduleSessionLogic'
import { PerfStatsEmitter } from './pages/PerfStatsPage/PerfStatsEmitter'

export type RoomScheduledRule = {
    icalStr: string
    origOptions: RepeatRule
    patches: Record<string, { scheduledEndTime: string }>
}

export type ScheduledSessionOfUser = {
    roomUrn: string
    name: string
    joinCode: string
    joinLink: string
    hostUrn: string
    host: { _id: string; username: string; isHost: boolean }
    users: { _id: string; username: string; isHost: boolean }[]
    isHost: boolean
    apiCreated: boolean
    hasPassword: boolean
    password?: string
    scheduledEndTime: string
    scheduledStartTime: string
    scheduledTimeZone: string
    scheduledRrule: RoomScheduledRule | null
    createdAt: string
    cloudRecordingEnabled: boolean
}

export type DashboardRoom = {
    _id: string
    createdAt: string
    host: { _id: string; username: string }
    hostUrn: string
    joinCode: string
    joinLink: string
    name: string
    updatedAt: string
}

export type ScreenRecordingConversionProgress = {
    directory: string
    fileName: string
    fullPath: string
    progress: number
}

export type AudioEngineDevSettings = {
    isLogToFileEnabled: boolean
    logLevel: 'verbose' | 'info'
    logOutputDirectory: string
    textLogFileName: string
    inputDeviceFramesPerBufferOverride: number
    outputDeviceFramesPerBufferOverride: number
    isAudioLogSender0Enabled: boolean
    isAudioLogSender1Enabled: boolean
    isAudioLogReceiver0Enabled: boolean
    isAudioLogReceiver1Enabled: boolean
    isAudioLogReceiver2Enabled: boolean
    isAudioLogPrimaryOutputEnabled: boolean
    tagUsername: string
    tagMuseappver: string
}

export type AppSettings = {
    remoteRecordingIsAdvancedMode: boolean
    remoteRecordingIsSimpleMode: boolean
    previewBeatsEnabled: boolean
    tempo: number
    timeSignatureTop: number
    timeSignatureBottom: number
    delta_dawcommand: number
    delta_alpha: number
    delta_beta: number
    remoteRecordingForwardCompensation: number
    remoteRecordingDisabled: boolean
    remoteRecordingSampleRate: number
    remoteRecordingIoBufferSize: number
    remoteRecordingRecorderCollectChunksTimeMs: number
    recordeeSaveLocalRecording: boolean
    recorderSaveRemoteRecording: boolean
    remoteRecordSaveDirectory: string

    noSelectDefaultCamera: boolean
    mirrorCamera: boolean
    noShowEmptyCamerasOnGallery: boolean
    applyHostGalleryPrefsToAllParticipants: boolean
    showWindowsDuringScreenshare: boolean
    screenShareDesktopVideoOptions: ExtraVideoOptions
    // on OSX, null should default to true.
    screenControlIgnoreScaleFactor: boolean | null
    screenControlWheelDirectionInvert: boolean
    screenControlWheelScaleFactor: number
    screenShareSideBarAlwaysOnTop: boolean

    screenRecordDesktop: { id: string; label: string }
    screenRecordingDesktopSourceInfo: DesktopCapturerSourceExt | null
    screenRecordOnlyAudio: boolean
    screenRecordSaveDirectory: string
    screenRecordVideoOptions: ExtraVideoOptions
    screenRecordProgram: 'builtin' /* | 'obs' */

    videoOptions: ExtraVideoOptions | null

    alwaysShareMuseDAWBridge: boolean
    forceShowAllAudioDevices: boolean
    forceAccessMidiForWindows: boolean
    preventVolumeAutoAdjustment: boolean
    selectSystemDefaultForAudioOutputOnStartup: boolean
    noMediaSourcesChangedPrompt: boolean
    autoChangeSystemAudioOutputDevice: boolean

    audioEngineDevSettings: AudioEngineDevSettings

    talkbackKeyMode: 'hold' | 'toggle'

    autoMuteThreshold: number
    autoMuteAttackMs: number
    autoMuteReleaseMs: number

    calendarIntegrationSelection: 'google' | ''

    newDevicePromptSuppressedLabels: { [key: string]: boolean }
    sampleRateMismatchPromptSuppressedLabels: { [key: string]: boolean }

    doNotShowRoomBoostedDialog: boolean
    dontShowAgainWhySessionLimit: boolean

    developerOptions: AppDeveloperOptions

    canaryFeatures: { [key in Features]?: boolean }
}

export type WindowTitleBar = {
    show: boolean
    mainWindowShowMinimize: boolean
    mainWindowShowMaximize: boolean
    mainWindowShowClose: boolean
}

export type ScreenRecordingInfo = {
    outputFilename: string
    outputFilenameBase: string
    outputFileFullPath: string
    outputFileDirectory: string
    proAudioFileFullPath: string
    recordingStartedTime: Date
}

export type VbAudioCableAutoDeselectionInfo = {
    deviceToDeselect: AudioInputStreamInfo
    selectedDevice: AudioSourceInfo
}

export type UpgradeToMuseProDialogType =
    | 'default'
    | 'sessionDurationLimitRunningOut'
    | 'sessionIsBoosted'
    | 'upgradedToPro'
    | 'boost'
    | 'sessionIsFull:host'
    | 'sessionIsFull:caller'
    | 'sessionIsFull:guest'
    | 'premiumFeature:noDeviceCountLimit'
    | 'premiumFeature:noCollaboratorCountLimit'
    | 'premiumFeature:useProAudio'
    | 'premiumFeature:noChannelCountLimit'
    | 'premiumFeature:museCamp'
    | 'premiumFeature:livestream'
    | 'premiumFeature:permanentRoom'
    | 'premiumFeature:realtimeSession'

export type UpgradeToMuseProDialogData = {
    type: UpgradeToMuseProDialogType
    forceBrowserModal?: boolean
    username?: string // used for type 'sessionIsFull:host'
}

export type AppSettingsPageMenuType = 'account' | 'about' | 'dev'

export type AppState = {
    /** @deprecated use sessionViewModeState.mode.fullscreen instead */
    isFullscreen: boolean
    wasRetractedBeforeFullscreen: boolean // goes with `isFullscreen`
    mostRecentRemoteRecordingInfo: { path: string; filename: string; directory: string } | null
    remoteDesktopBeingControlledStatus: { sessionId: string; username: string } | null
    screenRecordingConversionProgress: ScreenRecordingConversionProgress | null
    showVbAudioCableAutoDeselectionDialog: VbAudioCableAutoDeselectionInfo | null
    showAboutDialog: { startingPage: AppSettingsPageMenuType } | null
    showMediaPermissionsDialog: boolean
    showScreenControlDiagnosticsPage: boolean
    showAudioEngineDevSettingsPage: boolean
    showPerfStatsPage: boolean
    showScreenShareSelectionPage: { isOpen: boolean }
    showScheduleSessionDialog: {
        isOpen: boolean
        editingSession?: ScheduledSessionOfUser
        viewSession?: ScheduledSessionOfUser
        isSingleInSeries?: boolean
    }
    showAudioEngineSampleRateMismatchDialog: {
        selection: AudioInputSelection
        stats: AudioEngineLocalStreamStats
    } | null
    showAudioEngineStreamUnresponsiveDialog: {
        selection: AudioInputSelection
        stats: AudioEngineLocalStreamStats
    } | null
    showUpgradeToMuseProDialog: UpgradeToMuseProDialogData | null
    showTooManyHostedSessionsDialog: { isHost: boolean } | null
    showSessionIsBoostedDialog: boolean
    showAccountIsUpgradedDialog: boolean
    showAudioInputDeviceResolutionErrorDialog: AudioInputError | null
    showFreeTrialInfoDialog: boolean
    showChooseVirtualBackgroundModal: boolean
    isAppExitConfirmationDialogOpen: boolean
    sessionDurationLimitRunningOutDialogShown: { forEndTime: string } | null
    showScreenRecordingConversionDialog: { convertedFileInfo: ScreenRecordingInfo } | null
    showStateTriggersDialog: boolean
    appNotification: {
        title: string
        body: string
        muteAudioIfPossible?: boolean
        audioOnly?: boolean
    } | null
    musePluginState: { isValid: boolean; isConnected: boolean }
    isInDevMode: boolean
}

export type MessageDialogState = {
    id: string
    text: string
    positiveButton?: string | null // defaults to "OK" if not set
    negativeButton?: string | null // does not show if not set

    // if set, the negative button is enabled and its text is set as
    // "Don't show again"; when it is clicked, an entry in the localStorage
    // is made with this dialog's `id` to mark this dialog to not show again.
    negativeButtonDoNotShowAgain?: boolean
    textAlign?: 'left' | 'center' | 'right'
}

export type SnackbarSeverityType = 'error' | 'warning' | 'info' | 'success'

export type SnackbarData = {
    message: string
    severity: SnackbarSeverityType
    duration?: number
}

export type ExternalEvent = {
    eventName: string
    value: any
}

export type AppContextState = {
    appSettings: AppSettings
    windowTitleBar: WindowTitleBar
    appState: AppState
    rootSnackbarHistory: SnackbarData[]
    promptDialogState: MessageDialogState | null
    promptDialogResult: { [key: string]: any } | null
}

export type UpdateAppSettings = {
    readonly type: 'update_app_settings'
    readonly value: Partial<AppSettings>
}

export type UpdateWindowTitleBar = {
    readonly type: 'update_window_title_bar'
    readonly value: Partial<WindowTitleBar>
}

export type UpdateAppState = {
    readonly type: 'update_app_state'
    readonly value: Partial<AppState>
}

export type RootSnackbarHistoryAppend = {
    readonly type: 'root_snackbar_history_append'
    readonly value: SnackbarData
}

export type NewMessageDialog = {
    readonly type: 'new_message_dialog'
    readonly value: MessageDialogState
}

export type PromptDialogResult = {
    readonly type: 'prompt_dialog_result'
    readonly value: { [key: string]: any }
}

export type AppDeveloperOptions = {
    logLevel?: string
    enableAuditLogging?: boolean
    saveConfiguration?: boolean
    disableBlurEffect?: boolean
}

export type AppContextActions =
    | UpdateAppSettings
    | UpdateAppState
    | UpdateWindowTitleBar
    | RootSnackbarHistoryAppend
    | NewMessageDialog
    | PromptDialogResult

function loadInitialAppSettings() {
    const appSettingsDefault: AppSettings = {
        remoteRecordingIsAdvancedMode: false,
        remoteRecordingIsSimpleMode: true,
        previewBeatsEnabled: true,
        tempo: 120,
        timeSignatureTop: 4,
        timeSignatureBottom: 4,
        delta_dawcommand: 5,
        delta_alpha: 0,
        delta_beta: 60,
        remoteRecordingForwardCompensation: 50,
        remoteRecordingDisabled: false,
        remoteRecordingSampleRate: 44100,
        remoteRecordingIoBufferSize: 128,
        remoteRecordingRecorderCollectChunksTimeMs: 2000,
        recordeeSaveLocalRecording: false,
        recorderSaveRemoteRecording: true,
        remoteRecordSaveDirectory: '',

        noSelectDefaultCamera: false,
        mirrorCamera: true,
        noShowEmptyCamerasOnGallery: false,
        applyHostGalleryPrefsToAllParticipants: false,
        showWindowsDuringScreenshare: true,
        screenShareDesktopVideoOptions: { optionHeight: 1080, optionFramerate: 20 },
        screenControlIgnoreScaleFactor: null,
        screenControlWheelDirectionInvert: false,
        screenControlWheelScaleFactor: 1.0,
        screenShareSideBarAlwaysOnTop: true,

        screenRecordDesktop: { id: '_undef', label: '' },
        screenRecordingDesktopSourceInfo: null,
        screenRecordOnlyAudio: false,
        screenRecordSaveDirectory: '',
        screenRecordVideoOptions: {},
        screenRecordProgram: 'builtin',

        videoOptions: { optionFramerate: 30, optionWidth: 1080 },

        alwaysShareMuseDAWBridge: false,
        forceShowAllAudioDevices: false,
        forceAccessMidiForWindows: false,
        preventVolumeAutoAdjustment: false,
        selectSystemDefaultForAudioOutputOnStartup: false,
        noMediaSourcesChangedPrompt: false,
        autoChangeSystemAudioOutputDevice: true,

        audioEngineDevSettings: {
            isLogToFileEnabled: true,
            logLevel: 'info',
            logOutputDirectory: '',
            textLogFileName: '',
            inputDeviceFramesPerBufferOverride: 0,
            outputDeviceFramesPerBufferOverride: 0,
            isAudioLogSender0Enabled: false,
            isAudioLogSender1Enabled: false,
            isAudioLogReceiver0Enabled: false,
            isAudioLogReceiver1Enabled: false,
            isAudioLogReceiver2Enabled: false,
            isAudioLogPrimaryOutputEnabled: false,
            tagUsername: '',
            tagMuseappver: '',
        },

        talkbackKeyMode: 'hold',

        autoMuteThreshold: 0.5,
        autoMuteAttackMs: 100,
        autoMuteReleaseMs: 200,

        calendarIntegrationSelection: '',

        newDevicePromptSuppressedLabels: {},
        sampleRateMismatchPromptSuppressedLabels: {},

        doNotShowRoomBoostedDialog: false,
        dontShowAgainWhySessionLimit: false,

        developerOptions: {},
        canaryFeatures: {},
    }

    const obj = localStorage.getItem('appContext.appSettings')
    if (obj == null) {
        // set default object.
        localStorage.setItem('appContext.appSettings', JSON.stringify(appSettingsDefault))
        return appSettingsDefault
    } else {
        try {
            const appSettings = JSON.parse(obj) as AppSettings

            // If a new key is introduced in `appSettingsDefault` that does not exist in the
            // exising cache, then add that property into the cache.
            let changed = false
            Object.keys(appSettingsDefault).forEach((key) => {
                // @ts-ignore
                if (appSettings[key] == null) {
                    // @ts-ignore
                    appSettings[key] = appSettingsDefault[key]
                    changed = true
                }
            })

            if (changed) {
                localStorage.setItem('appContext.appSettings', JSON.stringify(appSettings))
            }

            return appSettings
        } catch (e) {
            // set default object.
            localStorage.setItem('appContext.appSettings', JSON.stringify(appSettingsDefault))
            return appSettingsDefault
        }
    }
}
const initialAppContext: AppContextState = {
    appSettings: loadInitialAppSettings(),
    windowTitleBar: {
        show: true,
        mainWindowShowMinimize: true,
        mainWindowShowMaximize: true,
        mainWindowShowClose: true,
    },
    appState: {
        isFullscreen: false,
        wasRetractedBeforeFullscreen: false,
        mostRecentRemoteRecordingInfo: null,
        showAccountIsUpgradedDialog: false,
        remoteDesktopBeingControlledStatus: null,
        screenRecordingConversionProgress: null,
        showVbAudioCableAutoDeselectionDialog: null,
        showAboutDialog: null,
        showMediaPermissionsDialog: false,
        showScreenControlDiagnosticsPage: false,
        showAudioEngineDevSettingsPage: false,
        showPerfStatsPage: false,
        showScreenShareSelectionPage: { isOpen: false },
        showScheduleSessionDialog: { isOpen: false },
        showAudioEngineSampleRateMismatchDialog: null,
        showAudioEngineStreamUnresponsiveDialog: null,
        showUpgradeToMuseProDialog: null,
        showTooManyHostedSessionsDialog: null,
        showSessionIsBoostedDialog: false,
        showAudioInputDeviceResolutionErrorDialog: null,
        showFreeTrialInfoDialog: false,
        showChooseVirtualBackgroundModal: false,
        isAppExitConfirmationDialogOpen: false,
        sessionDurationLimitRunningOutDialogShown: null,
        showScreenRecordingConversionDialog: null,
        showStateTriggersDialog: false,
        appNotification: null,
        musePluginState: { isValid: false, isConnected: false },
        isInDevMode: false,
    },
    rootSnackbarHistory: [],
    promptDialogState: null,
    promptDialogResult: null,
}

function appContextReducer(state: AppContextState, action: AppContextActions): AppContextState {
    console.log(`update AppContext: action.type="${action.type}" action.value=%O`, action.value)

    PerfStatsEmitter.inc('appContextDispatchCt')

    switch (action.type) {
        case 'update_app_settings':
            let t: any = {}
            if (action.value.remoteRecordingIsAdvancedMode === true) {
                t.remoteRecordingIsSimpleMode = false
            } else if (action.value.remoteRecordingIsSimpleMode === true) {
                t.remoteRecordingIsAdvancedMode = false
            }
            const next = {
                ...state.appSettings,
                ...action.value,
                ...t,
            }
            localStorage.setItem('appContext.appSettings', JSON.stringify(next))
            return {
                ...state,
                appSettings: next,
            }
        case 'update_window_title_bar':
            return {
                ...state,
                windowTitleBar: {
                    ...state.windowTitleBar,
                    ...action.value,
                },
            }
        case 'update_app_state':
            return {
                ...state,
                appState: {
                    ...state.appState,
                    ...action.value,
                },
            }
        case 'root_snackbar_history_append':
            return {
                ...state,
                rootSnackbarHistory: [...state.rootSnackbarHistory, action.value],
            }
        case 'new_message_dialog':
            return {
                ...state,
                promptDialogState: { ...action.value },
            }
        case 'prompt_dialog_result':
            return {
                ...state,
                promptDialogResult: { ...action.value },
            }
        default:
            // @ts-ignore
            neverReached(action)
            return state
    }
}
function neverReached(never: never) {}

export type AppContextType = {
    state: AppContextState
    dispatch: React.Dispatch<AppContextActions>
}
export const AppContext = React.createContext<AppContextType | undefined>(undefined)

export const AppContextProvider = ({ children }: any) => {
    const [state, dispatch] = useReducer(appContextReducer, initialAppContext)
    return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>
}

export const useAppContext = (): [AppContextState, React.Dispatch<AppContextActions>] => {
    const context = useContext(AppContext)

    if (context === null) {
        throw new Error('useAppContext must be used within a UserContextProvider')
    }
    return [context?.state!, context?.dispatch!]
}
